/*
 * Structure-aware, order-insensitive diff of two Speedy timetable exports.
 *
 * The original compare() in HistoricalTimetables.vue XOR'd Object.values(...)
 * using JSON.stringify() equality. That was fragile:
 *   - sensitive to object key ordering AND array ordering inside a record, so an
 *     export that Synergetic happened to emit in a different order looked "changed"
 *   - could only ever report added/removed: a record that changed a single field
 *     showed up as BOTH a removal (old value) and an addition (new value)
 *   - only looked at students / class_timeslots / teachers, ignoring the rest of
 *     the file (yard_duties, teacher_classes, heads_of_house, canvas_exceptions)
 *
 * This module instead matches records by a stable identity key, so it can report
 * added / removed / changed (with field-level detail), and it normalises key and
 * array ordering (see canonical()) so re-ordered-but-identical exports diff clean.
 *
 * Pure and dependency-free on purpose, so it can be unit tested in plain Node.
 */

const DAYS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function dayName(n) {
    return DAYS[n] || ('Day ' + n);
}

/*
 * Stable, order-insensitive serialisation. Object keys are sorted and arrays are
 * sorted, so two values that differ only in ordering serialise identically.
 * undefined is folded to null so a missing field and an explicit null compare equal.
 */
export function canonical(value) {
    if (Array.isArray(value)) {
        return '[' + value.map(canonical).sort().join(',') + ']';
    }
    if (value && typeof value === 'object') {
        return '{' + Object.keys(value).sort()
            .map(key => JSON.stringify(key) + ':' + canonical(value[key]))
            .join(',') + '}';
    }
    return JSON.stringify(typeof value === 'undefined' ? null : value);
}

// Normalise a section to an array of records, tolerating array | object | null/missing.
// (loadEverything.js already notes yard_duties is sometimes an object, not an array.)
function asList(value) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return Object.values(value);
    return [];
}

function isPrimitiveArray(v) {
    return Array.isArray(v) && v.every(x => x === null || typeof x !== 'object');
}

/*
 * Field-by-field changes between two records, ignoring the identity fields.
 * Arrays of primitives (eg: a student's `subjects`) are compared as sets and the
 * change is reported as the items added / removed, so ordering never matters.
 */
function fieldChanges(oldRec, newRec, ignore) {
    const ignoreSet = new Set(ignore || []);
    const names = new Set([...Object.keys(oldRec), ...Object.keys(newRec)]);
    const changes = [];
    names.forEach(field => {
        if (ignoreSet.has(field)) return;
        const from = oldRec[field];
        const to = newRec[field];
        if (canonical(from) === canonical(to)) return;
        if (isPrimitiveArray(from) || isPrimitiveArray(to)) {
            const fromArr = Array.isArray(from) ? from : [];
            const toArr = Array.isArray(to) ? to : [];
            changes.push({
                field,
                added: toArr.filter(x => fromArr.indexOf(x) === -1),
                removed: fromArr.filter(x => toArr.indexOf(x) === -1),
            });
        } else {
            changes.push({ field, from, to });
        }
    });
    return changes;
}

function indexByKey(list, keyFn) {
    const map = new Map();
    list.forEach(record => {
        const key = keyFn(record);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(record);
    });
    return map;
}

// Diff a single section of the export using its identity key.
function diffSection(section, olderData, newerData) {
    const olderList = section.getList(olderData);
    const newerList = section.getList(newerData);
    const oldMap = indexByKey(olderList, section.keyFn);
    const newMap = indexByKey(newerList, section.keyFn);
    const keys = new Set([...oldMap.keys(), ...newMap.keys()]);

    const added = [];
    const removed = [];
    const changed = [];
    let unchanged = 0;

    keys.forEach(key => {
        const olds = oldMap.get(key) || [];
        const news = newMap.get(key) || [];

        if (!olds.length) {
            news.forEach(r => added.push({ key, label: section.labelFn(r), record: r }));
            return;
        }
        if (!news.length) {
            olds.forEach(r => removed.push({ key, label: section.labelFn(r), record: r }));
            return;
        }
        if (olds.length === 1 && news.length === 1) {
            const changes = fieldChanges(olds[0], news[0], section.ignore);
            if (changes.length) {
                changed.push({ key, label: section.labelFn(news[0]), changes });
            } else {
                unchanged++;
            }
            return;
        }
        // Rare: the identity key is not unique within a section. Fall back to a
        // canonical multiset comparison so we still notice a real change.
        if (canonical(olds.map(canonical).sort()) !== canonical(news.map(canonical).sort())) {
            changed.push({
                key,
                label: section.labelFn(news[0] || olds[0]),
                changes: [{ field: '(multiple records)', from: olds.length + ' record(s)', to: news.length + ' record(s)' }],
            });
        } else {
            unchanged++;
        }
    });

    return {
        key: section.key,
        title: section.title,
        added,
        removed,
        changed,
        unchanged,
        total: added.length + removed.length + changed.length,
    };
}

/*
 * Section definitions describe the actual structure of a timetable export: how to
 * pull the list of records out of the file, the stable identity key for a record,
 * a human label, and which fields are the identity (so they are not reported as
 * "changed"). Everything else on a record is diffed automatically, so new fields
 * added to the export in future are picked up without touching this file.
 */
const SECTIONS = [
    {
        key: 'students',
        title: 'Students',
        // `students` is an object keyed by an internal id; StudentID is the stable identity.
        getList: d => asList(d.students),
        keyFn: r => 'S:' + r.StudentID,
        labelFn: r => ([r.StudentPreferred || r.StudentPreferredFormal, r.StudentSurname]
            .filter(Boolean).join(' ') || 'Student') + ' (' + r.StudentID + ')',
        ignore: ['StudentID'],
    },
    {
        key: 'teachers',
        title: 'Teachers',
        // Keyed by StaffID, the Synergetic id (same as the object key). SchoolStaffCode is
        // NOT the identity: it can be blank/reused, which would conflate different teachers.
        getList: d => asList(d.teachers),
        keyFn: r => 'T:' + r.StaffID,
        labelFn: r => ([r.StaffPreferredName, r.StaffSurname]
            .filter(Boolean).join(' ') || 'Teacher') + ' (' + (r.SchoolStaffCode || r.StaffID) + ')',
        ignore: ['StaffID'],
    },
    {
        key: 'class_timeslots',
        title: 'Class lessons',
        // A lesson is identified by its class, cycle day and period; teacher/room/description can change.
        getList: d => asList(d.class_timeslots),
        keyFn: r => ['L', r.class_code, r.cycle_day_index, r.period_short_name].join('|'),
        labelFn: r => r.class_code + ' · ' + dayName(r.cycle_day_index) + ' L' + r.period_short_name
            + (r.ClassDescription ? ' · ' + r.ClassDescription : ''),
        ignore: ['class_code', 'cycle_day_index', 'period_short_name'],
    },
    {
        key: 'teacher_classes',
        title: 'Teacher class allocations',
        getList: d => asList(d.teacher_classes),
        keyFn: r => ['TC', r.SchoolStaffCode, r.ClassCode].join('|'),
        labelFn: r => r.SchoolStaffCode + ' → ' + r.ClassCode,
        ignore: ['SchoolStaffCode', 'ClassCode'],
    },
    {
        key: 'yard_duties',
        title: 'Yard duties',
        // Keyed by teacher + day + start time; the location/description can change.
        getList: d => asList(d.yard_duties),
        keyFn: r => ['YD', r.SchoolStaffCode, r.DayNumber, r.DefinitionTimeFrom].join('|'),
        labelFn: r => r.SchoolStaffCode + ' · ' + dayName(r.DayNumber) + ' ' + (r.DefinitionDescription || '')
            + (r.ClassDescription ? ' @ ' + r.ClassDescription : ''),
        ignore: ['SchoolStaffCode', 'DayNumber', 'DefinitionTimeFrom'],
    },
    {
        key: 'heads_of_house',
        title: 'Heads of house',
        // `heads_of_house` maps a house code to a list of staff codes; treat each house as a record.
        getList: d => Object.entries((d && d.heads_of_house) || {}).map(([house, staff]) => ({ house, staff })),
        keyFn: r => 'HOH:' + r.house,
        labelFn: r => 'House ' + r.house,
        ignore: ['house'],
    },
    {
        key: 'canvas_exceptions',
        title: 'Canvas exceptions',
        // Null when there are none; otherwise a list keyed by the excepted class code.
        getList: d => asList(d && d.canvas_exceptions),
        keyFn: r => 'CX:' + (r.ClassCode || canonical(r)),
        labelFn: r => r.ClassCode || canonical(r),
        ignore: ['ClassCode'],
    },
];

/*
 * Diff two whole timetable exports. `older` and `newer` are the parsed JSON files;
 * the result reports what changed going from older -> newer.
 *
 * Returns { sections: [...], total, identical }.
 * Each section: { key, title, added[], removed[], changed[], unchanged, total }.
 */
export function diffTimetables(older, newer) {
    older = older || {};
    newer = newer || {};
    const sections = SECTIONS.map(section => diffSection(section, older, newer));
    const total = sections.reduce((sum, section) => sum + section.total, 0);
    return { sections, total, identical: total === 0 };
}
